import React, { useCallback, useState } from 'react';
import Cropper, { Area } from 'react-easy-crop';
import 'react-easy-crop/react-easy-crop.css';
import { getCroppedImageBlob } from '../branding/cropImage';
import { NAV_LOGO_ASPECT } from '../branding/navLogoAspect';

/** Re-export for callers that need the same ratio as the header logo slot. */
export const LOGO_CROP_ASPECT = NAV_LOGO_ASPECT;

export interface LogoCropModalProps {
  imageSrc: string;
  title: string;
  onCancel: () => void;
  /** Called with a PNG file ready to upload */
  onConfirmCropped: (file: File) => void;
  /** Upload the original file without cropping */
  onUploadOriginal: (file: File) => void;
  originalFile: File;
}

const LogoCropModal: React.FC<LogoCropModalProps> = ({
  imageSrc,
  title,
  onCancel,
  onConfirmCropped,
  onUploadOriginal,
  originalFile,
}) => {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  const [working, setWorking] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onCropComplete = useCallback((_: Area, pixels: Area) => {
    setCroppedAreaPixels(pixels);
  }, []);

  const handleUseCrop = async () => {
    if (!croppedAreaPixels) return;
    setError(null);
    setWorking(true);
    try {
      const blob = await getCroppedImageBlob(imageSrc, croppedAreaPixels);
      const base = originalFile.name.replace(/\.[^.]+$/, '') || 'logo';
      const file = new File([blob], `${base}-cropped.png`, { type: 'image/png' });
      onConfirmCropped(file);
    } catch (e) {
      console.error(e);
      setError('Could not create cropped image. Try “Upload original” instead.');
    } finally {
      setWorking(false);
    }
  };

  return (
    <div className="fixed z-50 inset-0 flex items-center justify-center p-4 bg-black/60">
      <div
        className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] flex flex-col border border-gray-200 dark:border-gray-600"
        role="dialog"
        aria-modal="true"
        aria-labelledby="logo-crop-title"
      >
        <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-600">
          <h3 id="logo-crop-title" className="text-lg font-medium text-gray-900 dark:text-gray-100">
            {title}
          </h3>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
            The crop frame matches the <strong>top navigation</strong> logo area: about{' '}
            <strong>{NAV_LOGO_ASPECT}:1</strong> (width to height), same ballpark as <strong>4:1</strong> to{' '}
            <strong>5:1</strong>. Drag and zoom so your wordmark fills that wide strip—then it will fill the bar instead of
            sitting in a small patch of empty space.
          </p>
        </div>

        <div className="relative w-full h-[min(360px,50vh)] bg-gray-900">
          <Cropper
            image={imageSrc}
            crop={crop}
            zoom={zoom}
            aspect={LOGO_CROP_ASPECT}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onCropComplete={onCropComplete}
          />
        </div>

        <div className="px-4 py-2 border-t border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-900/50">
          <label className="flex items-center gap-3 text-sm text-gray-700 dark:text-gray-300">
            <span className="shrink-0 w-14">Zoom</span>
            <input
              type="range"
              min={1}
              max={3}
              step={0.05}
              value={zoom}
              onChange={(e) => setZoom(Number(e.target.value))}
              className="flex-1 accent-[var(--color-brand)]"
            />
          </label>
        </div>

        {error && (
          <div className="px-4 py-2 text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20">{error}</div>
        )}

        <div className="px-4 py-3 flex flex-wrap gap-2 justify-end border-t border-gray-200 dark:border-gray-600">
          <button
            type="button"
            onClick={onCancel}
            disabled={working}
            className="px-4 py-2 text-sm font-medium rounded-md border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => onUploadOriginal(originalFile)}
            disabled={working}
            className="px-4 py-2 text-sm font-medium rounded-md border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600"
          >
            Upload original (no crop)
          </button>
          <button
            type="button"
            onClick={handleUseCrop}
            disabled={working || !croppedAreaPixels}
            className="px-4 py-2 text-sm font-medium rounded-md text-white bg-brand hover:bg-brand-hover focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand disabled:opacity-50"
          >
            {working ? 'Processing…' : 'Use cropped image'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default LogoCropModal;
